import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { service } from "@ember/service";
import { registerDestructor } from "@ember/destroyable";
import type Owner from "@ember/owner";
import { on } from "@ember/modifier";
import { pageTitle } from "ember-page-title";
import AudioVisualizer, {
  type VisualizerMode,
  type VisualizerGradient,
} from "#components/audio-visualizer.gts";
import type AudioService from "#services/audio.ts";
import { Sound } from "#models/sound.ts";
import { fn } from "@ember/helper";

const eq = <T,>(a: T, b: T): boolean => a === b;
const ms = (s: number) => Math.floor(s * 1000);
const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

export const MODE_OPTIONS: { value: VisualizerMode; label: string }[] = [
  { value: 0, label: "0 - Discrete frequencies" },
  { value: 1, label: "1 - 1/24th octave bands" },
  { value: 2, label: "2 - 1/12th octave bands" },
  { value: 3, label: "3 - 1/8th octave bands" },
  { value: 4, label: "4 - 1/6th octave bands" },
  { value: 5, label: "5 - 1/4th octave bands" },
  { value: 6, label: "6 - 1/3rd octave bands" },
  { value: 7, label: "7 - Half octave bands" },
  { value: 8, label: "8 - Full octave bands" },
  { value: 10, label: "10 - Graph" },
] as const;

export const GRADIENT_OPTIONS: { value: VisualizerGradient; label: string }[] = [
  { value: "classic", label: "Classic" },
  { value: "prism", label: "Prism" },
  { value: "rainbow", label: "Rainbow" },
  { value: "orangered", label: "OrangeRed" },
  { value: "steelblue", label: "SteelBlue" },
] as const;

export default class VisualizerSandboxComponent extends Component {
  @service declare audio: AudioService;

  @tracked mode: VisualizerMode = 1;
  @tracked gradient: string = "classic";
  @tracked isRunning = false;
  @tracked currentSound: Sound | null = null;
  @tracked soundDelay = 400;

  modeOptions = MODE_OPTIONS;
  gradientOptions = GRADIENT_OPTIONS;

  private timerId: ReturnType<typeof setInterval> | null = null;
  private currentIndex = 0;

  constructor(owner: Owner, args: object) {
    super(owner, args);
    registerDestructor(this, () => {
      this.stop();
    });
  }

  handleModeChange = (event: Event) => {
    const target = event.target as HTMLSelectElement;
    this.mode = Number(target.value) as VisualizerMode;
  };

  handleGradientChange = (event: Event) => {
    const target = event.target as HTMLSelectElement;
    this.gradient = target.value;
  };

  togglePlayback = () => {
    if (this.isRunning) {
      this.stop();
    } else {
      this.start();
    }
  };

  start = () => {
    this.isRunning = true;
    this.currentIndex = 0;
    this.playCurrentSound();

    this.timerId = setInterval(() => {
      this.currentIndex = (this.currentIndex + 1) % Sound.all.length;
      this.playCurrentSound();
    }, this.soundDelay);
  };

  stop = () => {
    this.isRunning = false;
    this.currentSound = null;
    if (this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  };

  changeDelay = (diff: number) => {
    const newDelay = this.soundDelay + diff;
    this.soundDelay = clamp(newDelay, 50, 5000);
    this.stop();
    this.start();
  };

  private playCurrentSound = () => {
    const sound = Sound.all[this.currentIndex];
    if (sound) {
      this.currentSound = sound;
      this.audio.playSound(sound);
    }
  };

  <template>
    {{pageTitle "Visualizer"}}

    <div class="flex flex-col gap-6 max-w-4xl mx-auto p-4" data-test-visualizer-sandbox>
      <AudioVisualizer @mode={{this.mode}} @gradient={{this.gradient}} class="h-64" />

      <div class="flex flex-col gap-4">
        <div class="flex flex-wrap items-center gap-6 text-sm text-slate-200">
          <label class="flex items-center gap-2">
            <span>Mode:</span>
            <select
              data-test-mode-select
              class="bg-slate-900 border border-slate-700 text-white rounded px-2.5 py-1.5 focus:outline-none focus:border-slate-500"
              {{on "change" this.handleModeChange}}
            >
              {{#each this.modeOptions as |opt|}}
                <option value={{opt.value}} selected={{if (eq this.mode opt.value) true false}}>
                  {{opt.label}}
                </option>
              {{/each}}
            </select>
          </label>

          <label class="flex items-center gap-2">
            <span>Gradient:</span>
            <select
              data-test-gradient-select
              class="bg-slate-900 border border-slate-700 text-white rounded px-2.5 py-1.5 focus:outline-none focus:border-slate-500"
              {{on "change" this.handleGradientChange}}
            >
              {{#each this.gradientOptions as |opt|}}
                <option value={{opt.value}} selected={{if (eq this.gradient opt.value) true false}}>
                  {{opt.label}}
                </option>
              {{/each}}
            </select>
          </label>
        </div>
        <div class="flex flex-wrap items-center gap-6 text-sm text-slate-200">
          <button
            type="button"
            data-test-playback-toggle
            class="px-2 py-1.5 bg-slate-800 hover:bg-slate-700 text-white border border-slate-600 rounded cursor-pointer font-medium"
            {{on "click" this.togglePlayback}}
          >
            {{#if this.isRunning}}
              Stop
            {{else}}
              Start
            {{/if}}
          </button>

          <div class="w-40 font-mono">
            {{this.soundDelay}}ms between
          </div>

          <button
            type="button"
            data-test-playback-toggle
            class="px-2 py-1.5 bg-slate-800 hover:bg-slate-700 text-white border border-slate-600 rounded cursor-pointer font-medium"
            {{on "click" (fn this.changeDelay -50)}}
          >
            Faster
          </button>
          <button
            type="button"
            data-test-playback-toggle
            class="px-2 py-1.5 bg-slate-800 hover:bg-slate-700 text-white border border-slate-600 rounded cursor-pointer font-medium"
            {{on "click" (fn this.changeDelay 50)}}
          >
            Slower
          </button>

          <div class="text-slate-400" data-test-current-sound>
            Sound:
            <span class="text-white font-mono text-lg">
              {{#if this.currentSound}}
                <span
                  class="rounded px-2 {{this.currentSound.colorClass}}"
                >{{this.currentSound.name}}</span>

                ({{ms this.currentSound.duration}}
                ms)
              {{else}}
                none
              {{/if}}
            </span>
          </div>
        </div>
      </div>
    </div>
  </template>
}
